import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format } from "date-fns";
import axios from "axios";
import { useTranslation } from "react-i18next";

const EmailHistory = ({ open, onClose, candidateEmail, recruiterId }) => {
  const { t } = useTranslation();
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (open && candidateEmail && recruiterId) {
      fetchEmailHistory(page);
    }
  }, [open, candidateEmail, recruiterId, page]);

  const fetchEmailHistory = async (pageNumber) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}recruiters/${recruiterId}/email-history/`,
        {
          params: {
            email: candidateEmail,
            page: pageNumber,
            page_size: 10, // Match with backend page_size
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      // With DRF pagination, the response structure changes
      setEmailHistory(response.data.results);

      // Calculate total pages from count and page size
      const count = response.data.count;
      const pageSize = 10; // Should match backend page_size
      setTotalPages(Math.ceil(count / pageSize));

      setError(null);
    } catch (err) {
      console.error("Error fetching email history:", err);
      setError(t("emailHistory.failedToLoadEmailHistory"));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0px 8px 32px rgba(0, 23, 231, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2.5,
          px: 3,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t("emailHistory.emailHistoryFor", { email: candidateEmail })}
        </Typography>
        <IconButton 
          onClick={onClose} 
          aria-label="close"
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" py={4}>
            {error}
          </Typography>
        ) : emailHistory.length === 0 ? (
          <Typography align="center" py={4} color="text.secondary">
            {t("emailHistory.noEmailsSent")}
          </Typography>
        ) : (
          <>
            <List>
              {emailHistory.map((email, index) => (
                <React.Fragment key={email.id || index}>
                  <Paper
                    elevation={0}
                    sx={{ 
                      my: 2, 
                      borderRadius: 2, 
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 12px rgba(0, 23, 231, 0.1)",
                      },
                    }}
                  >
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`email-content-${index}`}
                        id={`email-header-${index}`}
                        sx={{ px: 3, py: 2 }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {email.subject}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t("emailHistory.sentOn")}{" "}
                            {format(new Date(email.sent_at), "PPP 'at' HH:mm")}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography
                          variant="body1"
                          component="div"
                          sx={{
                            "& a": { color: "#0017E7" },
                            "& img": { maxWidth: "100%" },
                          }}
                          dangerouslySetInnerHTML={{ __html: email.body }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                </React.Fragment>
              ))}
            </List>

            {/* Pagination control */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3} mb={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2, 
            px: 3, 
            py: 1.2,
            borderColor: "#0017E7",
            color: "#0017E7",
            fontWeight: 500,
            "&:hover": {
              borderColor: "#0010a3",
              backgroundColor: "rgba(0, 23, 231, 0.04)",
            },
          }}
        >
          {t("emailHistory.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailHistory;